import { ABP, ListService, PagedResultDto, TrackByService } from '@abp/ng.core';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { DateAdapter } from '@abp/ng.theme.shared/extensions';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import type {
  GetAssetsInput,
  AssetWithNavigationPropertiesDto,
} from '../../../proxy/assets/models';
import { AssetService } from '../../../proxy/assets/asset.service';

@Component({
  selector: 'lib-asset',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [ListService, { provide: NgbDateAdapter, useClass: DateAdapter }],
  templateUrl: './asset.component.html',
  styles: [],
})
export class AssetComponent implements OnInit {
  data: PagedResultDto<AssetWithNavigationPropertiesDto> = {
    items: [],
    totalCount: 0,
  };

  filters = {} as GetAssetsInput;

  form: FormGroup;

  isFiltersHidden = true;

  isModalBusy = false;

  isModalOpen = false;

  selected?: AssetWithNavigationPropertiesDto;

  constructor(
    public readonly list: ListService,
    public readonly track: TrackByService,
    public readonly service: AssetService,
    private confirmation: ConfirmationService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    const getData = (query: ABP.PageQueryParams) =>
      this.service.getList({
        ...query,
        ...this.filters,
        filterText: query.filter,
      });

    const setData = (list: PagedResultDto<AssetWithNavigationPropertiesDto>) => (this.data = list);

    this.list.hookToQuery(getData).subscribe(setData);
  }

  clearFilters() {
    this.filters = {} as GetAssetsInput;
  }

  buildForm() {
    const {
      name,
      description,
      brand,
      invoiceData,
      purshaseDate,
      garrantyExpirationDate,
      serialNumber,
      internalCode,
      assetTypeId,
      assetStatusId,
      assetModelId,
      providerId,
      inventoryLocationId,
      legalDocumentId,
    } = this.selected?.asset || {};

    this.form = this.fb.group({
      name: [name ?? null, [Validators.required]],
      description: [description ?? null, []],
      brand: [brand ?? null, []],
      invoiceData: [invoiceData ?? null, []],
      purshaseDate: [purshaseDate ? new Date(purshaseDate) : null, []],
      garrantyExpirationDate: [garrantyExpirationDate ? new Date(garrantyExpirationDate) : null, []],
      serialNumber: [serialNumber ?? null, []],
      internalCode: [internalCode ?? null, []],
      assetTypeId: [assetTypeId ?? null, [Validators.required]],
      assetStatusId: [assetStatusId ?? null, [Validators.required]],
      assetModelId: [assetModelId ?? null, [Validators.required]],
      providerId: [providerId ?? null, []],
      inventoryLocationId: [inventoryLocationId ?? null, []],
      legalDocumentId: [legalDocumentId ?? null, []],
    });
  }

  hideForm() {
    this.isModalOpen = false;
    this.form.reset();
  }

  showForm() {
    this.buildForm();
    this.isModalOpen = true;
  }

  submitForm() {
    if (this.form.invalid) return;

    const request = this.selected
      ? this.service.update(this.selected.asset.id, this.form.value)
      : this.service.create(this.form.value);

    this.isModalBusy = true;

    request
      .pipe(
        finalize(() => (this.isModalBusy = false)),
        tap(() => this.hideForm()),
      )
      .subscribe(this.list.get);
  }

  create() {
    this.selected = undefined;
    this.showForm();
  }

  update(record: AssetWithNavigationPropertiesDto) {
    this.selected = record;
    this.showForm();
  }

  delete(record: AssetWithNavigationPropertiesDto) {
    this.confirmation.warn(
      'Inventory::DeleteConfirmationMessage',
      'Inventory::AreYouSure',
      { messageLocalizationParams: [] }
    ).pipe(
      filter(status => status === Confirmation.Status.confirm),
      switchMap(() => this.service.delete(record.asset.id)),
    ).subscribe(this.list.get);
  }
}
